import {
  ID,
  IDPrefixes,
  RelationshipUpdate,
  ReorderItem,
  ReorderResult,
  Task,
  TaskListItemTypes,
  untaggedId,
} from '@griply/types';

import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from '../prefix-id';
import { filterReorderableChildIds } from './filter-reorderable-child-ids';

export type OnTagReorderOptions = {
  updatedItems: readonly ReorderItem[];
  oldItems: readonly ReorderItem[];
  tasksMap: Record<ID, Task>;
  relationshipsMap: Record<ID, ID[]>;
  taskIdResolver?: (id: ID) => ID;
};

const identity = (id: ID): ID => id;

export const onTagReorder = ({
  updatedItems,
  oldItems,
  tasksMap,
  relationshipsMap,
  taskIdResolver = identity,
}: OnTagReorderOptions): ReorderResult => {
  const updatedSections: ReorderResult['updatedSections'] = [];
  const relationshipUpdates: RelationshipUpdate[] = [];

  updatedItems.forEach((item) => {
    if (item.type === TaskListItemTypes.Task) {
      const taskId = taskIdResolver(item.id);
      if (!tasksMap[taskId]) {
        return;
      }

      const newSectionItem = updatedItems.find(
        (section) =>
          section.type === TaskListItemTypes.Tag &&
          section.children?.includes(item.id),
      );
      const oldSectionItem = oldItems.find(
        (section) =>
          section.type === TaskListItemTypes.Tag &&
          section.children?.includes(taskId),
      );

      if (!newSectionItem || !oldSectionItem) {
        return;
      }

      const newSectionId = newSectionItem.id;
      const oldSectionId = oldSectionItem.id;

      if (newSectionId === oldSectionId) {
        return;
      }

      const taskPrefixedId = createPrefixId(taskId, IDPrefixes.Task);
      const relatedIds = relationshipsMap[taskPrefixedId] ?? [];
      const currentTagIds = relatedIds
        .filter((id) => getPrefixedIdPrefix(id) === IDPrefixes.Tag)
        .map((id) => getPrefixedId(id));

      if (oldSectionId !== untaggedId && currentTagIds.includes(oldSectionId)) {
        relationshipUpdates.push({
          id: createPrefixId(oldSectionId, IDPrefixes.Tag),
          remove: [taskPrefixedId],
        });
      }

      if (
        newSectionId !== untaggedId &&
        !currentTagIds.includes(newSectionId)
      ) {
        relationshipUpdates.push({
          id: createPrefixId(newSectionId, IDPrefixes.Tag),
          add: [taskPrefixedId],
        });
      }
      return;
    }

    if (item.type === TaskListItemTypes.Tag) {
      updatedSections.push({
        id: item.id,
        taskSorting: filterReorderableChildIds(
          item.children?.map(taskIdResolver),
        ),
      });
    }
  });

  return {
    updatedSections,
    updatedTasks: [],
    relationshipUpdates: relationshipUpdates.length
      ? relationshipUpdates
      : undefined,
  };
};
